import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, RefreshCw, AlertTriangle, Minus, Plus, Save } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Sorted display order of categories on the page
const CATEGORY_ORDER = ["Books", "Merch", "Apparel", "Bundles"];

const stockColor = (current, starting) => {
  if (starting === 0) return { bar: "#457B9D", text: "#457B9D" };
  const pct = (current / starting) * 100;
  if (pct <= 0) return { bar: "#E63946", text: "#E63946" };
  if (pct < 25) return { bar: "#E63946", text: "#E63946" };
  if (pct < 50) return { bar: "#E9C46A", text: "#B8860B" };
  return { bar: "#2A9D8F", text: "#2A9D8F" };
};

const AdminInventoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSku, setSavingSku] = useState(null);
  // local drafts per sku for the "Set to" input
  const [drafts, setDrafts] = useState({});
  const [savedFlash, setSavedFlash] = useState(null);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/inventory`, { withCredentials: true });
      setItems(data.items || []);
    } catch (err) {
      console.error("Failed to fetch inventory", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const patchInventory = async (sku, payload) => {
    setSavingSku(sku);
    try {
      const { data } = await axios.put(`${API}/api/admin/inventory/${sku}`, payload, {
        withCredentials: true
      });
      setItems((prev) => prev.map((it) => (it.sku === sku ? { ...it, ...data } : it)));
      setSavedFlash(sku);
      setTimeout(() => setSavedFlash((s) => (s === sku ? null : s)), 1200);
    } catch (err) {
      console.error("Failed to update inventory", err);
      alert("Failed to save. Please try again.");
    } finally {
      setSavingSku(null);
    }
  };

  const handleAdjust = (sku, delta) => patchInventory(sku, { adjust: delta });
  const handleSetTo = (sku) => {
    const raw = drafts[sku];
    if (raw === undefined || raw === "") return;
    const value = Math.max(0, parseInt(raw, 10) || 0);
    patchInventory(sku, { current_stock: value });
    setDrafts((d) => ({ ...d, [sku]: "" }));
  };
  const handleSaveMeta = (sku, patch) => patchInventory(sku, patch);

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    rows: items.filter((it) => it.category === cat)
  })).filter((g) => g.rows.length > 0);

  const totalCurrent = items.reduce((s, it) => s + (it.current_stock || 0), 0);
  const totalStarting = items.reduce((s, it) => s + (it.starting_stock || 0), 0);
  const lowStock = items.filter((it) => it.starting_stock > 0 && it.current_stock / it.starting_stock < 0.25);

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <Link to="/admin/dashboard" className="text-[#1D3557] font-bold flex items-center gap-2 mb-2 hover:text-[#E63946]">
              <ArrowLeft className="w-5 h-5" /> Back to Dashboard
            </Link>
            <h1 className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Inventory
            </h1>
            <p className="text-[#1D3557] font-medium">
              {totalCurrent} of {totalStarting} units on hand across {items.length} SKUs.
            </p>
          </div>
          <button
            onClick={fetchInventory}
            className="btn-secondary text-base py-3 px-6 inline-flex items-center gap-2"
            data-testid="refresh-inventory-btn"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>

        {/* Low-stock alert */}
        {lowStock.length > 0 && (
          <div className="tactile-card p-4 mb-6 border-[#E63946]" data-testid="low-stock-alert">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-[#E63946] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {lowStock.length} SKU{lowStock.length > 1 ? "s" : ""} running low (below 25%)
                </p>
                <p className="text-[#1D3557]/80 text-sm font-medium mt-1">
                  {lowStock.map((it) => it.name).join(" · ")}
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map((group) => (
              <section key={group.category} data-testid={`inv-group-${group.category}`}>
                <h2 className="text-2xl font-bold text-[#1D3557] mb-4 inline-flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <Package className="w-6 h-6 text-[#2A9D8F]" />
                  {group.category}
                </h2>
                <div className="space-y-3">
                  {group.rows.map((it) => {
                    const c = stockColor(it.current_stock, it.starting_stock);
                    const pct = it.starting_stock > 0 ? Math.min(100, Math.round((it.current_stock / it.starting_stock) * 100)) : 0;
                    const busy = savingSku === it.sku;
                    const flashed = savedFlash === it.sku;
                    return (
                      <motion.div
                        key={it.sku}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="tactile-card p-4"
                        data-testid={`inv-row-${it.sku}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <p className="font-bold text-[#1D3557]">{it.name}</p>
                              <span className="text-xs font-mono px-2 py-0.5 rounded bg-[#1D3557]/10 text-[#1D3557]">{it.sku}</span>
                              <span className="text-xs font-bold text-[#1D3557]/60">${it.price.toFixed(2)}</span>
                              {flashed && <span className="text-xs font-bold text-[#2A9D8F]">Saved ✓</span>}
                            </div>
                            {/* Progress bar */}
                            <div className="mt-1 h-3 rounded-full bg-[#1D3557]/10 overflow-hidden">
                              <div
                                className="h-full transition-all"
                                style={{ width: `${pct}%`, backgroundColor: c.bar }}
                                data-testid={`inv-bar-${it.sku}`}
                              />
                            </div>
                            <div className="flex items-center justify-between mt-1 text-xs font-medium">
                              <span style={{ color: c.text }} className="font-bold" data-testid={`inv-count-${it.sku}`}>
                                {it.current_stock} of {it.starting_stock} left ({pct}%)
                              </span>
                              {it.current_stock === 0 && it.starting_stock > 0 && (
                                <span className="text-[#E63946] font-bold">Out of stock</span>
                              )}
                            </div>
                          </div>

                          {/* Controls */}
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="inline-flex items-center rounded-xl border-2 border-[#1D3557] bg-white overflow-hidden">
                              <button
                                type="button"
                                onClick={() => handleAdjust(it.sku, -1)}
                                disabled={busy || it.current_stock <= 0}
                                className="p-2 text-[#1D3557] hover:bg-[#E63946] hover:text-white disabled:opacity-40 transition-colors"
                                data-testid={`inv-minus-${it.sku}`}
                                aria-label={`Subtract 1 from ${it.name}`}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={drafts[it.sku] ?? ""}
                                placeholder={String(it.current_stock)}
                                onChange={(e) => setDrafts((d) => ({ ...d, [it.sku]: e.target.value }))}
                                onKeyDown={(e) => e.key === "Enter" && handleSetTo(it.sku)}
                                className="w-16 text-center font-bold text-[#1D3557] focus:outline-none"
                                data-testid={`inv-set-${it.sku}`}
                              />
                              <button
                                type="button"
                                onClick={() => handleAdjust(it.sku, +1)}
                                disabled={busy}
                                className="p-2 text-[#1D3557] hover:bg-[#2A9D8F] hover:text-white disabled:opacity-40 transition-colors"
                                data-testid={`inv-plus-${it.sku}`}
                                aria-label={`Add 1 to ${it.name}`}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            {drafts[it.sku] !== undefined && drafts[it.sku] !== "" && (
                              <button
                                type="button"
                                onClick={() => handleSetTo(it.sku)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-[#1D3557] text-white font-bold text-xs hover:bg-[#2A9D8F] transition-colors"
                                data-testid={`inv-save-${it.sku}`}
                              >
                                <Save className="w-4 h-4" />
                                Set to {drafts[it.sku]}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const val = window.prompt(`Restock ${it.name}\nEnter new starting_stock (currently ${it.starting_stock}):`, String(it.starting_stock));
                                if (val === null) return;
                                const num = Math.max(0, parseInt(val, 10) || 0);
                                handleSaveMeta(it.sku, { starting_stock: num });
                              }}
                              disabled={busy}
                              className="text-xs font-bold text-[#457B9D] hover:text-[#1D3557] underline"
                              data-testid={`inv-restart-${it.sku}`}
                            >
                              Restock…
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInventoryPage;
